// "Next ticket": step through the list you opened a ticket from.
//
// The Dashboard (and My Day) remember the ordered ids of the list the agent is
// looking at. The ticket page uses that to show "3 of 57 · Unassigned", to step
// with [ and ], and, after Resolve / Snooze / Send, to open the next ticket
// instead of dropping the agent back at the top of the list.
//
// Kept in sessionStorage (per tab, gone when the tab closes) and ignored after
// two hours, so a list can never step you through tickets from a stale view.
// A ticket opened from a link or an email has no list, so none of this appears.
//
// Pure on purpose (storage is passed in): tests/ticket-nav.test.ts.

export interface ListContext {
  /** Ticket ids in the order the list showed them (first 500). */
  ids: string[];
  /** "Unassigned", "Mine", "Needs attention" ... shown next to the position. */
  label: string;
  /** Where "back to the list" goes (path + query, inside this app). */
  returnTo: string;
  /** The list has more tickets than the ids saved here (paging / the 500 cap). */
  hasMore: boolean;
  savedAt: number;
}

export const LIST_CONTEXT_KEY = 'pa-ticket-nav';
export const LIST_CONTEXT_TTL_MS = 2 * 60 * 60 * 1000;
export const LIST_CONTEXT_MAX_IDS = 500;
const AUTO_ADVANCE_KEY = 'pa-auto-advance';

type ReadStore = Pick<Storage, 'getItem'> | null;
type WriteStore = Pick<Storage, 'setItem'> | null;

export function saveListContext(
  input: Omit<ListContext, 'savedAt'>,
  storage: WriteStore,
  now: number = Date.now(),
): void {
  try {
    const ctx: ListContext = {
      ...input,
      ids: input.ids.slice(0, LIST_CONTEXT_MAX_IDS),
      savedAt: now,
    };
    storage?.setItem(LIST_CONTEXT_KEY, JSON.stringify(ctx));
  } catch {
    /* blocked or full storage: next / previous simply don't appear */
  }
}

export function loadListContext(storage: ReadStore, now: number = Date.now()): ListContext | null {
  try {
    const raw = storage?.getItem(LIST_CONTEXT_KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as Partial<ListContext> | null;
    if (!v || !Array.isArray(v.ids) || !v.ids.every((x) => typeof x === 'string')) return null;
    if (typeof v.label !== 'string' || typeof v.savedAt !== 'number') return null;
    // Only a path inside this app: this value becomes a navigation target.
    if (typeof v.returnTo !== 'string' || !v.returnTo.startsWith('/') || v.returnTo.startsWith('//')) return null;
    if (now - v.savedAt > LIST_CONTEXT_TTL_MS) return null;
    return { ids: v.ids, label: v.label, returnTo: v.returnTo, hasMore: v.hasMore === true, savedAt: v.savedAt };
  } catch {
    return null;
  }
}

export interface Neighbours {
  index: number;
  total: number;
  prev: string | null;
  next: string | null;
}

/** Where `id` sits in the remembered list, or null if it isn't in it. */
export function neighbours(ctx: ListContext | null, id: string | undefined): Neighbours | null {
  if (!ctx || !id) return null;
  const index = ctx.ids.indexOf(id);
  if (index === -1) return null;
  return {
    index,
    total: ctx.ids.length,
    prev: index > 0 ? (ctx.ids[index - 1] ?? null) : null,
    next: index < ctx.ids.length - 1 ? (ctx.ids[index + 1] ?? null) : null,
  };
}

export function dropFromContext(ctx: ListContext, id: string): ListContext {
  return ctx.ids.includes(id) ? { ...ctx, ids: ctx.ids.filter((x) => x !== id) } : ctx;
}

/** After finishing `id`: open the next ticket, or go back to the list at its end.
 *  The finished ticket is dropped from the remembered list so [ and ] skip it. */
export function planAdvance(
  ctx: ListContext | null,
  id: string | undefined,
): { to: string; ctx: ListContext } | null {
  const nb = neighbours(ctx, id);
  if (!ctx || !id || !nb) return null;
  return {
    to: nb.next ? `/tickets/${nb.next}` : ctx.returnTo,
    ctx: dropFromContext(ctx, id),
  };
}

const ADVANCING = new Set([
  'resolve',
  'close',
  'archive',
  'snooze_1h',
  'snooze_4h',
  'snooze_tomorrow',
  'snooze_monday',
  'snooze_2d',
  'snooze_custom',
]);

/** Actions that take the ticket out of your queue, so "open the next one" makes sense. */
export function advancesList(action: string): boolean {
  return ADVANCING.has(action);
}

/** "Open the next ticket after Resolve / Send": on unless the agent turned it off. */
export function readAutoAdvance(storage: ReadStore): boolean {
  try {
    return storage?.getItem(AUTO_ADVANCE_KEY) !== '0';
  } catch {
    return true;
  }
}

export function writeAutoAdvance(storage: WriteStore, on: boolean): void {
  try {
    storage?.setItem(AUTO_ADVANCE_KEY, on ? '1' : '0');
  } catch {
    /* not remembered */
  }
}
